import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { RefreshCw, RotateCcw, XCircle, Loader2, ExternalLink } from 'lucide-react';

export default function AdminPlaywright() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadRuns(); }, []);

  const loadRuns = async () => {
    try {
      const { data } = await api.get('/admin/playwright-runs');
      setRuns(data.runs || []);
    } catch (err) {
      toast.error('Failed to load Playwright runs');
    } finally {
      setLoading(false);
    }
  };

  const doAction = async (runId, action) => {
    try {
      await api.post(`/admin/playwright-runs/${runId}/${action}`);
      toast.success(`Run ${action}ed`);
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
    }
  };

  const statusColor = (s) => ({
    queued: 'bg-gray-500/20 text-gray-400',
    waiting_for_human: 'bg-yellow-500/20 text-yellow-400',
    running: 'bg-blue-500/20 text-blue-400',
    success: 'bg-green-500/20 text-green-400',
    failed: 'bg-red-500/20 text-red-400',
    cancelled: 'bg-red-500/20 text-red-400',
  }[s] || 'bg-gray-500/20 text-gray-400');

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#00a884]" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Playwright Runs</h2>
          <p className="text-[#8696a0] text-sm mt-1">Execution logs, retry, and cancel controls</p>
        </div>
        <button onClick={loadRuns} className="p-2 text-[#8696a0] hover:text-white transition"><RefreshCw className="w-4 h-4" /></button>
      </div>

      <div className="bg-[#111b21] rounded-xl border border-[#2a3942] overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[#8696a0] text-xs uppercase border-b border-[#2a3942]">
              <th className="px-5 py-3 text-left">Run ID</th>
              <th className="px-5 py-3 text-left">Case</th>
              <th className="px-5 py-3 text-left">Profile</th>
              <th className="px-5 py-3 text-left">Status</th>
              <th className="px-5 py-3 text-left">Retries</th>
              <th className="px-5 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-8 text-center text-[#8696a0]">No Playwright runs yet</td></tr>
            ) : runs.map(r => (
              <tr key={r.id} className="border-b border-[#2a3942] hover:bg-[#1a242a]">
                <td className="px-5 py-3 text-white text-xs font-mono">{(r.id || '').slice(0, 8)}</td>
                <td className="px-5 py-3 text-[#8696a0] text-sm font-mono">{(r.case_id || '').slice(0, 8)}</td>
                <td className="px-5 py-3 text-[#8696a0] text-xs">{(r.automation_profile_id || '').slice(0, 8)}</td>
                <td className="px-5 py-3"><span className={`px-2 py-1 rounded-full text-xs ${statusColor(r.status)}`}>{r.status}</span></td>
                <td className="px-5 py-3 text-[#8696a0] text-sm">{r.retry_count || 0}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    {(r.status === 'failed' || r.status === 'cancelled') && (
                      <button onClick={() => doAction(r.id, 'retry')} className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700">
                        <RotateCcw className="w-3 h-3" /> Retry
                      </button>
                    )}
                    {(r.status === 'queued' || r.status === 'waiting_for_human' || r.status === 'running') && (
                      <button onClick={() => doAction(r.id, 'cancel')} className="flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700">
                        <XCircle className="w-3 h-3" /> Cancel
                      </button>
                    )}
                    {r.status === 'success' && <span className="text-green-400 text-xs">Completed</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
